import { format } from 'date-fns';

const SEC_TITLES = {
  hourly: 'In-Process Checks / इन-प्रोसेस',
  daily_lab: 'Lab — Daily Destructive Sample / लैब (प्रतिदिन)',
  per_heat: 'Lab — Per Heat / प्रति हीट (LHB parts only)',
};

const esc = (s) => String(s ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
const fmtD = (iso) => { const d = new Date(iso); return isNaN(d.getTime()) ? (iso || '—') : format(d, 'dd/MM/yyyy'); };

const sectionTable = (insp, secId, specs, readings) => {
  const cols = readings.map((r) => esc(r.time || r.label || '')).join('</th><th>');
  const rows = specs.map((s) => {
    const cells = readings.map((r) => {
      const c = r.values?.[s.key];
      if (!c || c.value == null || c.value === '') return '<td></td>';
      const oldV = r.edited ? r.original_values?.[s.key]?.value : null;
      const changed = oldV != null && oldV !== '' && String(oldV) !== String(c.value);
      const suffix = changed ? `<div class="was">was ${esc(oldV)}</div>` : '';
      const locsHtml = Array.isArray(c.locations) && c.locations.length
        ? `<div class="locs">${c.locations.map((L) => `<span class="loc${L.passed === false ? ' fail' : ''}">${esc(L.label || '')}:${esc(L.value)}</span>`).join(' ')}</div>` : '';
      return `<td class="${c.passed === false ? 'fail' : ''}${changed ? ' edited' : ''}">${esc(c.value)}${locsHtml}${suffix}</td>`;
    }).join('');
    const spec = s.spec_display || (s.type === 'attribute' ? 'Ok / Not-Ok' : '');
    return `<tr><td class="ch">${esc(s.name_en)}<br><span class="hi">${esc(s.name_hi || '')}</span></td><td class="sp">${esc(spec)}</td>${cells}</tr>`;
  }).join('');
  const remarks = readings.map((r) => `<td class="sm">${esc(r.remarks || '')}</td>`).join('');
  const recBy = readings.map((r) => `<td class="sm">${esc(r.recorded_by_name || '')}${r.corrected_by_name ? `<br><span class="corr">✎ ${esc(r.corrected_by_name)}${r.correction_reason ? ` — ${esc(r.correction_reason)}` : ''}</span>` : ''}</td>`).join('');
  return `
  <div class="sec">
    <div class="sec-title">${SEC_TITLES[secId] || ''} <span class="sm">(${readings.length} ${secId === 'hourly' ? 'readings' : 'entries'})</span></div>
    <table>
      <tr><th class="ch">Characteristic / कैरेक्टरिस्टिक</th><th class="sp">Specification</th><th>${cols || '&nbsp;'}</th></tr>
      ${rows}
      <tr><td class="ch">Remarks / रिमार्क्स</td><td class="sp"></td>${remarks || '<td></td>'}</tr>
      <tr><td class="ch">Recorded By / हस्ताक्षर</td><td class="sp"></td>${recBy || '<td></td>'}</tr>
    </table>
  </div>`;
};

export function printChecksheet(insp) {
  const secOf = (x) => x.section || 'hourly';
  const allSpecs = [...(insp.specs || [])].sort((a, b) => a.display_order - b.display_order);
  const allReadings = insp.readings || [];
  const sections = ['hourly', 'daily_lab', 'per_heat'].filter((id) => allSpecs.some((s) => secOf(s) === id));
  const tables = sections.map((id) =>
    sectionTable(insp, id, allSpecs.filter((s) => secOf(s) === id), allReadings.filter((r) => secOf(r) === id))
  ).join('');

  const heatText = (insp.heat_numbers && insp.heat_numbers.length) ? insp.heat_numbers.join(', ') : '—';
  const info = [
    ['Job Card No.', insp.job_card_no], ['Drawing No.', insp.drawing_number || '—'],
    ['Heat / Lot No.', heatText],
    ['Date', fmtD(insp.date)], ['Shift', insp.shift || '—'],
    ['Machine No.', insp.machine_name ? `${insp.machine_no} — ${insp.machine_name}` : (insp.machine_no || '—')], ['Operator', insp.operator_name || '—'],
    ['Sample Size', `${insp.sample_size ?? '—'} / ${insp.frequency || '—'}`],
  ].map(([l, v]) => `<div class="info"><span>${l}</span><b>${esc(v)}</b></div>`).join('');

  const html = `<!doctype html><html><head><meta charset="utf-8">
<title>${esc(insp.doc_no || 'Checksheet')} — ${esc(insp.job_card_no || '')}</title>
<style>
@page { size: A4 landscape; margin: 9mm; }
* { box-sizing: border-box; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
body { font-family: Arial, 'Noto Sans Devanagari', sans-serif; font-size: 10px; color: #111; margin: 0; }
.head { display: flex; justify-content: space-between; align-items: flex-start; border: 1.2px solid #111; padding: 6px 10px; }
.co { font-size: 15px; font-weight: 800; letter-spacing: 0.5px; }
.ttl { text-align: center; }
.ttl .en { font-size: 13px; font-weight: 700; }
.ttl .hi { font-size: 10px; }
.doc { text-align: right; font-size: 9px; }
.infos { display: flex; flex-wrap: wrap; border: 1.2px solid #111; border-top: 0; }
.info { padding: 4px 10px; border-right: 1px solid #555; font-size: 9.5px; }
.info span { color: #444; margin-right: 5px; text-transform: uppercase; font-size: 8px; }
.sec { margin-top: 8px; }
.sec-title { font-weight: 700; font-size: 10.5px; margin-bottom: 2px; }
table { border-collapse: collapse; width: 100%; }
th, td { border: 1px solid #333; padding: 2.5px 4px; text-align: center; font-size: 9px; }
th { background: #eee; font-weight: 700; }
td.ch, th.ch { text-align: left; width: 130px; font-weight: 600; }
td.sp, th.sp { text-align: left; width: 85px; }
.hi { font-weight: 400; color: #444; font-size: 8px; }
.sm { font-size: 8px; color: #333; }
td.fail { font-weight: 800; background: #ddd; text-decoration: underline; }
td.edited { background: #fff7e6; }
.was { font-size: 7.5px; color: #b45309; text-decoration: line-through; margin-top: 1px; font-weight: 500; }
.corr { color: #b45309; font-size: 7.5px; font-style: italic; }
.locs { display: block; font-size: 7px; color: #475569; margin-top: 1px; line-height: 1.2; }
.loc { display: inline-block; margin: 0 2px; }
.loc.fail { color: #b91c1c; font-weight: 700; text-decoration: underline; }
.sign { display: flex; justify-content: space-between; margin-top: 18px; }
.sig { width: 30%; border-top: 1px solid #111; padding-top: 3px; font-size: 9.5px; text-align: center; }
.foot { display: flex; justify-content: space-between; margin-top: 10px; font-size: 8px; color: #444; }
</style></head><body>
<div class="head">
  <img class="co" src="https://customer-assets-wrfwihn1.emergentagent.net/job_7886508f-18cf-499c-9228-09581ab9ee95/artifacts/b13a0301b3439545_ABOK_SPRINGS_LOGO_navy.webp" alt="ABOK Springs" style="height:24px;" />
  <div class="ttl">
    <div class="en">${esc(insp.title_en || `Daily Validation Checksheet — ${insp.stage_name}`)}</div>
    <div class="hi">${esc(insp.title_hi || '')}</div>
  </div>
  <div class="doc">Doc No.: <b>${esc(insp.doc_no || '—')}</b><br>Status: <b>${insp.status === 'verified' ? 'VERIFIED' : insp.status === 'lab_pending' ? 'VERIFIED (LAB PENDING)' : 'IN PROGRESS'}</b></div>
</div>
<div class="infos">${info}</div>
${tables}
<div class="sign">
  <div class="sig">Operator / ऑपरेटर<br><b>${esc(insp.operator_name || '')}</b></div>
  <div class="sig">Inspector / निरीक्षक<br><b>${esc(insp.inspector_name || '')}</b></div>
  <div class="sig">Verified By / सत्यापित<br><b>${esc(insp.verified_by_name || '')}</b>${insp.verified_at ? ` — ${fmtD(insp.verified_at)}` : ''}</div>
  ${sections.includes('per_heat') ? `<div class="sig">Lab Sign-off / लैब<br><b>${esc(insp.lab_verified_by_name || '')}</b>${insp.lab_verified_at ? ` — ${fmtD(insp.lab_verified_at)}` : ''}</div>` : ''}
</div>
<div class="foot"><span>Format No.: ${esc(insp.doc_no || '—')}</span><span>Printed: ${format(new Date(), 'dd/MM/yyyy HH:mm')}</span></div>
</body></html>`;

  const iframe = document.createElement('iframe');
  iframe.setAttribute('style', 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;');
  document.body.appendChild(iframe);
  iframe.contentDocument.open();
  iframe.contentDocument.write(html);
  iframe.contentDocument.close();
  setTimeout(() => {
    if (!iframe.contentWindow) return;
    iframe.contentWindow.focus();
    iframe.contentWindow.print();
    setTimeout(() => iframe.remove(), 60000);
  }, 350);
}
